import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { FileText, Download, Trash2, Eye } from "lucide-react"
import { PropagateLoader } from "react-spinners"
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "@/components/ui/dialog" // Removed DialogTrigger as it's not used directly
import {
	CMEReport, // Use CMEReport type
	useDownloadCMEReport, // Use CME hooks
	useGetCMEReportPdfUrl,
} from "../../hooks/useCMEReports" // Point to the new hook file
import { toast } from "sonner"
import { PDFViewer } from "../pdf/PDFViewer"

// Status colors remain the same
const statusColors = {
	pending: "text-amber-500",
	in_progress: "text-blue-500",
	completed: "text-green-500",
	failed: "text-red-500",
}

// Interface for component props
interface CMEReportItemProps {
	report: CMEReport // Use CMEReport type
	caseId: string
	onDelete: (reportId: string) => void
}

const CMEReportItem: React.FC<CMEReportItemProps> = ({
	report,
	caseId,
	onDelete,
}) => {
	const [showPdfViewer, setShowPdfViewer] = useState(false)
	// Use the hook to get the PDF URL for the specific CME report
	const pdfUrl = useGetCMEReportPdfUrl(report.case_id, report.id)

	// Use the hook for downloading CME reports
	const downloadCMEReportMutation = useDownloadCMEReport()

	// Handler for downloading the CME report
	const handleDownloadCMEReport = async (reportToDownload: CMEReport) => {
		if (reportToDownload.status !== "completed") {
			toast.warning("CME report is not ready for download yet")
			return
		}

		try {
			await downloadCMEReportMutation.mutateAsync({
				caseId,
				reportId: reportToDownload.id,
				format: "docx",
			})
			// Optional: Add success toast if needed
			// toast.success("CME report downloaded successfully");
		} catch (error) {
			toast.error("Failed to download CME report")
		}
	}

	return (
		<>
			{/* Dialog for PDF Viewer */}
			<Dialog open={showPdfViewer} onOpenChange={setShowPdfViewer}>
				<DialogContent className="max-w-4xl h-[90vh] flex flex-col">
					<DialogHeader>
						<DialogTitle>
							CME Report - {/* Updated Title */}
							{new Date(report.created_at).toLocaleString(undefined, {
								dateStyle: "medium",
								timeStyle: "short",
							})}
						</DialogTitle>
					</DialogHeader>
					<div className="flex-grow overflow-auto">
						{pdfUrl ? (
							<PDFViewer url={pdfUrl} />
						) : (
							<div className="flex items-center justify-center h-full">
								<PropagateLoader color="#3b82f6" />
							</div>
						)}
					</div>
					<DialogFooter className="mt-2 pt-2 border-t">
						<div className="flex items-center space-x-2 w-full justify-between">
							<Button
								className="flex items-center space-x-1 px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
								disabled={
									report.status !== "completed" ||
									downloadCMEReportMutation.isPending
								}
								onClick={() => handleDownloadCMEReport(report)}
							>
								<Download size={16} className="mr-1" />
								<span>
									{downloadCMEReportMutation.isPending
										? "Downloading..."
										: "Download"}
								</span>
							</Button>
							<Button variant="outline" onClick={() => setShowPdfViewer(false)}>
								Close
							</Button>
						</div>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Main item display */}
			<div
				key={report.id}
				className="flex flex-col p-3 border rounded-md bg-background hover:bg-accent/50 transition-colors"
			>
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-3 flex-grow">
						<FileText className="h-5 w-5 text-muted-foreground" />
						<div className="w-full">
							<div className="font-medium">
								CME Report from {/* Updated Label */}
								{new Date(report.created_at).toLocaleString(undefined, {
									dateStyle: "medium",
									timeStyle: "short",
								})}
							</div>
							<div className="flex flex-row space-x-2">
								<div className="text-sm flex items-center space-x-1">
									<span className="text-muted-foreground">Status:</span>
									<span
										className={`font-medium ${
											statusColors[report.status as keyof typeof statusColors]
										}`}
									>
										{report.status.charAt(0).toUpperCase() +
											report.status.slice(1)}
									</span>
								</div>
								{report.status === "in_progress" && (
									<div className="flex items-center justify-center flex-grow">
										<div className="ml-[96px] mt-[-4px]">
											<PropagateLoader size={8} color="#3b82f6" />
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="flex space-x-2">
						<Button
							variant="ghost"
							size="sm"
							disabled={report.status !== "completed"}
							onClick={() => setShowPdfViewer(true)} // Open viewer on click
						>
							<Eye className="h-4 w-4 mr-1" />
							View
						</Button>
						<Button
							variant="ghost"
							size="sm"
							disabled={
								report.status !== "completed" ||
								downloadCMEReportMutation.isPending
							}
							onClick={() => handleDownloadCMEReport(report)}
						>
							{downloadCMEReportMutation.isPending ? (
								<PropagateLoader size={6} color="#3b82f6" className="mr-1" />
							) : (
								<Download className="h-4 w-4 mr-1" />
							)}
							{downloadCMEReportMutation.isPending ? "Downloading" : "Download"}
						</Button>
						<Button
							variant="ghost"
							size="sm"
							onClick={() => onDelete(report.id)} // Call delete handler from props
							className="text-destructive hover:text-destructive"
						>
							<Trash2 className="h-4 w-4 mr-1" />
							Delete
						</Button>
					</div>
				</div>
			</div>
		</>
	)
}

export default CMEReportItem